import './ContactWebform.css';

import { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import {
    useQuery,
    useLazyQuery,
    GET_ACCOUNT_BY_NAME,
    GET_ACCOUNTS_FILTER_BY_NAME_AND_ADDRESS,
    GET_ACCOUNTS_FILTER_BY_CONTACT_NAME_EMAIL_PHONE,
    GET_ACCOUNT_BY_ORDER,
    GET_CONTACTS_FILTER_BY_ACCOUNT_ID_AND_NAME_OR_EMAIL_OR_PHONE,
    GET_CONTACTS_BY_ACCOUNT_AND_ORDER_NUMBER,
    GET_BLOCKED_EMAILS,
    GET_LAST_ORDER_NUMBER,
    GET_ORDER_BY_EXTERNAL_ID,
    useMutation,
    CREATE_ORDER,
    GET_CAMPAIGNS_FOR_PROPERTY_WITH_NAME_AND_ACTIVE_ENDDATE,
    CREATE_ORDER_CAMPAIGN,
    DELETE_ORDER,
} from 'utils/graphql';
import Pill from 'components/Pill/Pill';
import SearchResultsList from 'components/SearchResultsList/SearchResultsList';
import InfoModal from 'components/Modals/InfoModal/InfoModal';
import { ReactComponent as AddIcon } from 'assets/icons/add_icon.svg';
import { ReactComponent as CopyIcon } from 'assets/icons/copy_icon_24.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/error_icon.svg';
import { ReactComponent as ExpandMoreIcon } from 'assets/icons/expand_more_icon_24.svg';
import CompanyFormModal from 'components/Modals/CompanyFormModal/CompanyFormModal';
import ContactFormModal from 'components/Modals/ContactFormModal/ContactFormModal';
import { v4 as uuidv4 } from 'uuid';
import User from 'utils/user.util';
import { generateId } from 'utils/text.util';
import { formatUTCDateTime } from 'utils/date.util';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import { useNavigate } from 'react-router-dom';
import OrdersDatatable from 'components/ContactWebform/OrdersDatatable/OrdersDatatable';
import { useTranslation } from 'react-i18next';
import TransactionHistoryTable from 'components/ContactWebform/TransactionHistoryTable/TransactionHistoryTable';

const ContactWebform = ({ property }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const searchByValues = [
        {
            name: 'company',
            type: 'company',
            placeholder: 'name_address',
        },
        {
            name: 'contact',
            type: 'contact',
            placeholder: 'name_email_phone',
        },
        {
            name: 'order',
            type: 'order',
            placeholder: 'order_number',
        },
    ];
    const [ivanhoeAccount, setIvanhoeAccount] = useState(null);
    const [clientType, setClientType] = useState('B2B');
    const [searchBy, setSearchBy] = useState(searchByValues[0]);
    const [companySearchQuery, setCompanySearchQuery] = useState('');
    const [companySearchResults, setCompanySearchResults] = useState({
        company: [],
        contact: [],
        order: [],
    });
    const [showCompanyResults, setShowCompanyResults] = useState(false);
    const [generateBtnEnabled, setGenerateBtnEnabled] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [contactSearchQuery, setContactSearchQuery] = useState('');
    const [contactSearchResults, setContactSearchResults] = useState({
        company: [],
        contact: [],
        order: [],
    });
    const [showContactResults, setShowContactResults] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [showTransactions, setShowTransactions] = useState(false);
    const [isOrdersTableLoaded, setIsOrdersTableLoaded] = useState(false);
    const [alert, setAlert] = useState(null);
    const [blocked, setBlocked] = useState([]);
    const [infoModalIsOpen, setInfoModalIsOpen] = useState(false);
    const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
    const [accountToEdit, setAccountToEdit] = useState(null);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [contactToEdit, setContactToEdit] = useState(null);
    const [vtsConfirmationCode, setVtsConfirmationCode] = useState('');
    const [vtsCodeError, setVtsCodeError] = useState('');
    const [isLoadingVtsCode, setIsLoadingVtsCode] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [showCampaignList, setShowCampaignList] = useState(false);
    const [codeCopySuccess, setCodeCopySuccess] = useState(false);

    const clientTypes = ['B2B', 'B2C'];

    useQuery(GET_BLOCKED_EMAILS, {
        variables: {
            limit: 9999,
        },
        onCompleted: (data) => {
            setBlocked(data.table.records.map((record) => record.blocked));
        },
    });

    const { loading: isLoadingCampaigns } = useQuery(
        GET_CAMPAIGNS_FOR_PROPERTY_WITH_NAME_AND_ACTIVE_ENDDATE,
        {
            fetchPolicy: 'cache-and-network',
            variables: {
                date: formatUTCDateTime(new Date(), false),
                propertyId: property.sfid,
            },
            onCompleted: (data) => {
                setCampaigns([
                    { name: t('none'), id: null },
                    ...data.table.records.map((record) => record.campaign),
                ]);
            },
            onError: (error) => {
                console.log(error);
            },
        }
    );

    useQuery(GET_ACCOUNT_BY_NAME, {
        variables: { account_name: 'ivanhoe cambridge inc' },
        onCompleted: (data) => {
            const account = data.table.records[0].account;
            setIvanhoeAccount(account);
            if (clientType === 'B2C') {
                setSelectedAccount(account);
            }
        },
    });

    const { loading: isLoadingAccountsByNameAddress, refetch: getAccountsFilterByNameAndAddress } =
        useQuery(GET_ACCOUNTS_FILTER_BY_NAME_AND_ADDRESS, {
            variables: { not_like: 'ivanhoe%' },
            notifyOnNetworkStatusChange: true,
            onCompleted: (data) => {
                if (searchBy.type === 'company') {
                    setCompanySearchResults({
                        ...companySearchResults,
                        company: data.table.records.map((record) => record.account),
                    });
                }
            },
        });

    const [getContacts, { loading: isLoadingContacts }] = useLazyQuery(
        GET_CONTACTS_FILTER_BY_ACCOUNT_ID_AND_NAME_OR_EMAIL_OR_PHONE,
        { fetchPolicy: 'network-only' }
    );

    const [
        getAccountsByContactNameEmailPhone,
        { loading: isLoadingAccountsByContactNameEmailPhone },
    ] = useLazyQuery(GET_ACCOUNTS_FILTER_BY_CONTACT_NAME_EMAIL_PHONE, {
        fetchPolicy: 'network-only',
    });

    const [getAccountsByOrder, { loading: isLoadingAccountsByOrder }] = useLazyQuery(
        GET_ACCOUNT_BY_ORDER,
        { fetchPolicy: 'network-only' }
    );

    const [getContactsByOrder, { loading: isLoadingContactsByOrder }] = useLazyQuery(
        GET_CONTACTS_BY_ACCOUNT_AND_ORDER_NUMBER,
        { fetchPolicy: 'network-only' }
    );

    const [getLastOrder] = useLazyQuery(GET_LAST_ORDER_NUMBER, {
        fetchPolicy: 'network-only',
    });

    const [getOrderByExternalId] = useLazyQuery(GET_ORDER_BY_EXTERNAL_ID, {
        fetchPolicy: 'network-only',
    });

    const [createOrder] = useMutation(CREATE_ORDER, {
        variables: {
            status: 'Draft',
            totalamount: 0,
            sfid: uuidv4(),
            property__c: property.sfid,
            propertyuser__c: User.getUser()?.sfid,
        },
    });

    const [createOrderCampaign] = useMutation(CREATE_ORDER_CAMPAIGN);
    const [deleteOrder] = useMutation(DELETE_ORDER);

    const orderWithExternalIdExists = async (externalId) => {
        const { data } = await getOrderByExternalId({
            variables: {
                external_id__c: externalId,
            },
        });

        return data.table.count > 0;
    };

    const generateVTSCode = async () => {
        window.focus();

        if (!selectedContact?.email || isEmailBlocked(selectedContact.email)) {
            return;
        }

        setVtsCodeError('');
        setIsLoadingVtsCode(true);
        let external_id__c = generateId();
        while (await orderWithExternalIdExists(external_id__c)) {
            external_id__c = generateId();
        }
        const { data, error } = await getLastOrder();
        if (error) {
            console.log({ error });
            setVtsCodeError(t('vts_code_fail'));
            setIsLoadingVtsCode(false);
        } else {
            const nowUtcString = formatUTCDateTime();
            const lastOrderNumber = data.table.records[0].order.ordernumber;
            const { data: createOrderData, error: createOrderError } = await createOrder({
                variables: {
                    createddate: nowUtcString,
                    effectivedate: nowUtcString,
                    external_id__c,
                    ordernumber: String(parseInt(lastOrderNumber) + 1),
                    accountid: selectedAccount.sfid,
                    contact__c: selectedContact.sfid,
                },
            });

            if (createOrderError) {
                setVtsCodeError(t('vts_code_fail'));
            } else if (selectedCampaign && selectedCampaign.id) {
                const { error: createOrderCampaignError } = await createOrderCampaign({
                    variables: {
                        orderid: createOrderData.createRow.order.sfid,
                        campaignid: selectedCampaign.id,
                    },
                });
                if (createOrderCampaignError) {
                    // rolling back created order
                    deleteOrder({ variables: { id: createOrderData.createRow.order.id } });
                    setVtsCodeError(t('vts_code_fail'));
                } else {
                    setVtsConfirmationCode(createOrderData.createRow.order.external_id__c);
                }
            } else {
                setVtsConfirmationCode(createOrderData.createRow.order.external_id__c);
            }

            setIsLoadingVtsCode(false);
            copyVtsCode(external_id__c);
        }
    };

    const onAccountCreateOrEdit = (account) => {
        setSelectedAccount(account);
        setCompanySearchQuery('');
    };

    const onContactCreateOrEdit = (contact, newAccount = null) => {
        setSelectedContact(contact);
        if (newAccount) {
            setSelectedAccount(newAccount);
        }
        if (contactSearchQuery) {
            setContactSearchQuery('');
        } else {
            searchContacts();
        }
    };

    const editAccount = (account) => {
        setAccountToEdit(account);
        setIsCompanyModalOpen(true);
    };

    const selectAccount = (account) => {
        setSelectedAccount(account);
        setShowCompanyResults(false);
        setShowTransactions(true);
        setIsOrdersTableLoaded(false);
        // when searching by contact or order, setting the contact automatically
        if (
            searchBy.type !== 'company' &&
            account.contact_list &&
            account.contact_list.length === 1
        ) {
            setSelectedContact(account.contact_list[0].contact);
        }
    };

    const selectContact = (contact) => {
        setSelectedContact(contact);
        setShowContactResults(false);
    };

    const editContact = (contact) => {
        setContactToEdit(contact);
        setIsContactModalOpen(true);
    };

    const clearSelectedAccount = () => {
        setSelectedAccount(null);
        setAccountToEdit(null);
        setSelectedContact(null);
        setContactToEdit(null);
        setShowTransactions(false);
    };

    const clearSelectedContact = () => {
        setSelectedContact(null);
        setContactToEdit(null);
        if (clientType === 'B2C') {
            setShowTransactions(false);
        }
    };

    const searchContacts = () => {
        if (!selectedAccount) return;
        if (clientType === 'B2B') {
            getContacts({
                variables: {
                    accountid: selectedAccount.sfid,
                    search: `%${contactSearchQuery}%`,
                },
                onCompleted: (data) => {
                    const contacts = data.table.records.map((record) => record.contact);
                    setContactSearchResults({
                        company: contacts,
                        contact: contacts,
                        order: contacts,
                    });
                },
            });
        } else {
            switch (searchBy.type) {
                case 'contact':
                    getContacts({
                        variables: {
                            accountid: selectedAccount.sfid,
                            search: `%${contactSearchQuery}%`,
                        },
                        onCompleted: (data) => {
                            setContactSearchResults({
                                ...contactSearchResults,
                                contact: data.table.records.map((record) => record.contact),
                            });
                        },
                    });
                    break;
                case 'order':
                    if (contactSearchQuery) {
                        getContactsByOrder({
                            variables: {
                                accountid: selectedAccount.sfid,
                                like: `${contactSearchQuery}%`,
                            },
                            onCompleted: (data) => {
                                setContactSearchResults({
                                    ...contactSearchResults,
                                    order: data.table.records.map((record) => record.contact),
                                });
                            },
                        });
                    }
                    break;
            }
        }
    };

    const searchCompanies = () => {
        switch (searchBy.type) {
            case 'company':
                getAccountsFilterByNameAndAddress({ like: `%${companySearchQuery}%` });
                break;
            case 'contact':
                getAccountsByContactNameEmailPhone({
                    variables: {
                        like: `%${companySearchQuery}%`,
                        not_like: 'ivanhoe%',
                    },
                    onCompleted: (data) => {
                        setCompanySearchResults({
                            ...companySearchResults,
                            contact: data.table.records.map((record) => record.account),
                        });
                    },
                });
                break;
            case 'order':
                if (companySearchQuery) {
                    getAccountsByOrder({
                        variables: {
                            like: `${companySearchQuery}%`,
                            not_like: 'ivanhoe%',
                        },
                        onCompleted: (data) => {
                            setCompanySearchResults({
                                ...companySearchResults,
                                order: data.table.records.map((record) => record.account),
                            });
                        },
                    });
                }
                break;
        }
    };

    const getCompanySearchResultText = (searchResult) => {
        if (searchBy.type === 'company') {
            return searchResult.name;
        } else if (searchBy.type === 'contact') {
            const text =
                searchResult.contact_list && searchResult.contact_list.length > 0
                    ? searchResult.contact_list[0].contact.name + ' @ '
                    : '';
            return text + searchResult.name;
        } else {
            // search by order
            const order = searchResult.order_list[0].order;
            let text = '';
            if (
                order.external_id__c.substring(0, companySearchQuery.length) === companySearchQuery
            ) {
                text = `#${order.external_id__c} @ `;
            } else if (
                order.vts_id.substring(0, companySearchQuery.length) === companySearchQuery
            ) {
                text = `#${order.vts_id} @ `;
            }
            return text + searchResult.name.toUpperCase();
        }
    };

    const getCompanySearchResultSubtext = (searchResult) => {
        if (searchBy.type === 'order') {
            return searchResult.contact_list && searchResult.contact_list.length > 0
                ? searchResult.contact_list[0].contact.name
                : null;
        }
        return searchResult.address_line_1__c;
    };

    const getContactSearchResultText = (searchResult) => {
        if (clientType === 'B2C' && searchBy.type === 'order') {
            const order = searchResult.order_list[0].order;
            let text = '';
            if (
                order.external_id__c.substring(0, contactSearchQuery.length) === contactSearchQuery
            ) {
                text = `#${order.external_id__c} ${t('by')} `;
            } else if (
                order.vts_id.substring(0, contactSearchQuery.length) === contactSearchQuery
            ) {
                text = `#${order.vts_id} ${t('by')} `;
            }
            return text + searchResult.name;
        }
        return searchResult.name;
    };

    const isLoadingCompanySearchResults = () => {
        switch (searchBy.type) {
            case 'company':
                return isLoadingAccountsByNameAddress;
            case 'contact':
                return isLoadingAccountsByContactNameEmailPhone;
            case 'order':
                return isLoadingAccountsByOrder;
        }
        return false;
    };

    const isLoadingContactSearchResults = () => {
        if (clientType === 'B2B' || searchBy.type === 'contact') {
            return isLoadingContacts;
        } else if (searchBy.type === 'order') {
            return isLoadingContactsByOrder;
        }
        return false;
    };

    const normalizeEmail = (email) => (email || '').trim().toLowerCase();

    const isEmailBlocked = (email) =>
        blocked.some((b) => b && normalizeEmail(b.email) === normalizeEmail(email));

    const validateSelectedContact = () => {
        setGenerateBtnEnabled(false);
        if (!selectedContact.email) {
            setAlert({
                type: 'danger',
                messages: [{ content: 'contact_missing_email', toTranslate: true }],
            });
            return;
        }
        if (isEmailBlocked(selectedContact.email)) {
            setAlert({
                type: 'danger',
                messages: [
                    { content: 'alert_purchase_prohibited', toTranslate: true },
                    {
                        content: (
                            <Button variant="dark" size="sm" className="ms-2">
                                {t('details')}
                            </Button>
                        ),
                        toTranslate: false,
                    },
                ],
                onClick: () => setInfoModalIsOpen(true),
            });
        } else {
            setGenerateBtnEnabled(true);
        }
    };

    const copyVtsCode = async (_code) => {
        try {
            if (typeof _code === 'string') {
                await navigator.clipboard.writeText(_code);
            } else {
                await navigator.clipboard.writeText(vtsConfirmationCode);
            }
            setCodeCopySuccess(true);
            setTimeout(() => {
                setCodeCopySuccess(false);
            }, 1500);
        } catch (error) {
            console.log('Error copying generated code.');
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => searchCompanies(), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [companySearchQuery]);

    useEffect(() => {
        const timer = setTimeout(() => searchContacts(), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [contactSearchQuery]);

    useEffect(() => {
        setContactSearchQuery('');
        if (clientType === 'B2B') {
            setCompanySearchQuery('');
            setCompanySearchResults({
                company: [],
                contact: [],
                order: [],
            });
            if (searchBy.type !== 'order') {
                searchCompanies();
            }
        } else {
            setContactSearchResults({
                company: [],
                contact: [],
                order: [],
            });
            if (searchBy.type !== 'order') {
                searchContacts();
            }
        }
    }, [searchBy]);

    useEffect(() => {
        setAlert(null);
        if (!selectedContact) {
            setGenerateBtnEnabled(false);
            return;
        }
        validateSelectedContact();
        if (clientType === 'B2C') {
            setShowTransactions(true);
        }
    }, [selectedContact, blocked]);

    useEffect(() => searchContacts(), [selectedAccount]);

    useEffect(() => {
        clearSelectedAccount();
        clearSelectedContact();
        setVtsConfirmationCode('');
        setSelectedCampaign(null);
        setGenerateBtnEnabled(false);
        setIsOrdersTableLoaded(false);
        if (clientType === 'B2B') {
            setSearchBy(searchByValues[0]);
            setSelectedAccount(null);
        } else {
            setSearchBy(searchByValues[1]);
            setSelectedAccount(ivanhoeAccount);
        }
    }, [clientType]);

    return (
        <div className="contact-webform">
            {alert && (
                <Alert
                    variant={alert.type}
                    className="mb-3 d-flex justify-content-center align-items-center"
                    onClick={alert.onClick ? () => alert.onClick() : undefined}
                    style={
                        alert.onClick
                            ? {
                                  cursor: 'pointer',
                              }
                            : undefined
                    }
                >
                    {alert.messages.map((element, i) => (
                        <span key={i}>
                            {element.toTranslate ? t(element.content) : element.content}
                        </span>
                    ))}
                </Alert>
            )}
            <div className="client-type-selection">
                {clientTypes.map((type, i) => (
                    <Button
                        key={i}
                        variant={clientType === type ? 'dark' : 'light'}
                        onClick={() => setClientType(type)}
                    >
                        {type}
                    </Button>
                ))}
            </div>
            <div className="contact-info">
                <h3>{t('contact_info')}</h3>
                <hr />
                <div className="company-form">
                    <div className="d-flex align-items-center mb-2">
                        {t('search')}:
                        <DropdownButton className="search-dropdown" title={t(searchBy.name)}>
                            {searchByValues.map((searchByValue, i) => {
                                if (clientType === 'B2C' && searchByValue.type === 'company')
                                    return;
                                return (
                                    <Dropdown.Item
                                        key={i}
                                        href="#"
                                        onClick={() => setSearchBy(searchByValue)}
                                    >
                                        {t(searchByValue.name)}
                                    </Dropdown.Item>
                                );
                            })}
                        </DropdownButton>
                    </div>
                    <div className="search-container mt-3">
                        {clientType === 'B2B' &&
                            (selectedAccount ? (
                                <Pill
                                    closeAction={clearSelectedAccount}
                                    objectToEdit={selectedAccount}
                                    editAction={editAccount}
                                >
                                    <div className="result-item">
                                        <span className="fw-bold">{selectedAccount.name}</span>
                                        {selectedAccount.address_line_1__c && (
                                            <span className="sub-text">
                                                ({selectedAccount.address_line_1__c})
                                            </span>
                                        )}
                                    </div>
                                </Pill>
                            ) : (
                                <>
                                    <Form.Control
                                        type="text"
                                        placeholder={t(searchBy.placeholder)}
                                        value={companySearchQuery}
                                        onChange={(e) => setCompanySearchQuery(e.target.value)}
                                        onClick={() =>
                                            !showCompanyResults && setShowCompanyResults(true)
                                        }
                                    />
                                    <SearchResultsList
                                        show={showCompanyResults}
                                        onClickOutside={() => {
                                            setShowCompanyResults(false);
                                        }}
                                        onClickCallback={(result) => selectAccount(result)}
                                        searchResults={companySearchResults[searchBy.type]}
                                        isLoadingResults={isLoadingCompanySearchResults()}
                                        getText={(result) => getCompanySearchResultText(result)}
                                        getSubText={(result) =>
                                            getCompanySearchResultSubtext(result)
                                        }
                                        topPosition="43px"
                                        onFooterClick={() => {
                                            setShowCompanyResults(false);
                                            setIsCompanyModalOpen(true);
                                        }}
                                    >
                                        <>
                                            <AddIcon /> {t('create_company')}
                                        </>
                                    </SearchResultsList>
                                </>
                            ))}
                        {selectedAccount && (
                            <div className="position-relative">
                                <Form.Group className="text-start">
                                    {clientType === 'B2B' && (
                                        <Form.Label>{t('contact')}</Form.Label>
                                    )}
                                    {selectedContact ? (
                                        <Pill
                                            closeAction={clearSelectedContact}
                                            objectToEdit={selectedContact}
                                            editAction={editContact}
                                        >
                                            {selectedContact.name}
                                        </Pill>
                                    ) : (
                                        <Form.Control
                                            type="text"
                                            placeholder={
                                                clientType === 'B2B'
                                                    ? undefined
                                                    : t(searchBy.placeholder)
                                            }
                                            value={contactSearchQuery}
                                            onChange={(e) => setContactSearchQuery(e.target.value)}
                                            onClick={() =>
                                                !showCompanyResults && setShowContactResults(true)
                                            }
                                        />
                                    )}
                                </Form.Group>
                                <SearchResultsList
                                    show={showContactResults}
                                    onClickOutside={() => {
                                        setShowContactResults(false);
                                    }}
                                    onClickCallback={(result) => selectContact(result)}
                                    searchResults={contactSearchResults[searchBy.type]}
                                    isLoadingResults={isLoadingContactSearchResults()}
                                    getText={(result) => getContactSearchResultText(result)}
                                    getSubText={(result) => {
                                        if (result.email) return result.email;
                                        return result.phone;
                                    }}
                                    topPosition={clientType === 'B2B' ? '75px' : '43px'}
                                    onFooterClick={() => {
                                        setShowContactResults(false);
                                        setIsContactModalOpen(true);
                                    }}
                                >
                                    <>
                                        <AddIcon /> {t('create_contact')}
                                    </>
                                </SearchResultsList>
                            </div>
                        )}
                        <Form.Group className="my-2">
                            <Form.Label>{t('campaign')}</Form.Label>
                            <div className="position-relative">
                                <Form.Control
                                    type="text"
                                    value={selectedCampaign ? selectedCampaign.name : ''}
                                    readOnly={true}
                                    onClick={() => !showCampaignList && setShowCampaignList(true)}
                                    className="campaign-dropdown"
                                />
                                <ExpandMoreIcon
                                    className="dropdown-icon"
                                    onClick={() => !showCampaignList && setShowCampaignList(true)}
                                />
                                <SearchResultsList
                                    show={showCampaignList}
                                    onClickOutside={() => {
                                        setShowCampaignList(false);
                                    }}
                                    onClickCallback={(result) => {
                                        setSelectedCampaign(result);
                                        setShowCampaignList(false);
                                    }}
                                    searchResults={campaigns}
                                    isLoadingResults={isLoadingCampaigns}
                                    getText={(result) => result.name}
                                    getSubText={(result) => result.description}
                                    topPosition="43px"
                                />
                            </div>
                        </Form.Group>
                        {vtsConfirmationCode && (
                            <>
                                <h3>{t('confirmation_code')}</h3>
                                <hr />
                                <div className="d-flex">
                                    <Form.Control
                                        type="text"
                                        value={vtsConfirmationCode}
                                        readOnly
                                        disabled
                                    />
                                    <Button
                                        variant={codeCopySuccess ? 'success' : 'secondary'}
                                        className={`ms-2 copy-button${codeCopySuccess ? ' copied' : ''}`}
                                        onClick={copyVtsCode}
                                    >
                                        <CopyIcon fill="white" className="copy-button-icon" />
                                        <span className="copy-button-label" aria-live="polite">
                                            {t('copied', 'Copied')}
                                        </span>
                                    </Button>
                                </div>
                            </>
                        )}
                        <LoadingButton
                            isLoading={isLoadingVtsCode}
                            isDisabled={!generateBtnEnabled || isLoadingCampaigns}
                            className="mt-2 w-100"
                            maxWidth="none"
                            onClick={generateVTSCode}
                        >
                            {t('generate_vts')}
                        </LoadingButton>
                        {vtsCodeError && (
                            <div
                                className="invalid-feedback mt-2 fw-bold"
                                style={{ display: vtsCodeError ? 'block' : 'none' }}
                            >
                                {vtsCodeError}
                            </div>
                        )}
                        {vtsConfirmationCode && (
                            <Button variant="dark" className="mt-2" onClick={() => navigate(0)}>
                                {t('reset')}
                            </Button>
                        )}
                        {showTransactions && (
                            <>
                                <h3>{t('transaction_history')}</h3>
                                <hr />
                                <TransactionHistoryTable
                                    accountId={selectedAccount ? selectedAccount.sfid : null}
                                    contactId={selectedContact ? selectedContact.sfid : null}
                                    isB2C={clientType === 'B2C'}
                                    loadTransactionHistory={isOrdersTableLoaded}
                                />
                                <OrdersDatatable
                                    accountId={selectedAccount ? selectedAccount.sfid : null}
                                    contactId={selectedContact ? selectedContact.sfid : null}
                                    isB2C={clientType === 'B2C'}
                                    setIsOrdersTableLoaded={setIsOrdersTableLoaded}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
            <InfoModal
                isOpen={infoModalIsOpen}
                setOpen={setInfoModalIsOpen}
                options={{
                    header: <ErrorIcon fill="var(--bs-red)" />,
                    center: true,
                }}
            >
                <p>
                    {t('clients_request_declined')}
                    <a href="mailto:CS-Support@incomm.com">CS-Support@incomm.com</a>
                </p>
            </InfoModal>
            <CompanyFormModal
                accountToEdit={accountToEdit}
                isOpen={isCompanyModalOpen}
                setIsOpen={setIsCompanyModalOpen}
                onSuccess={onAccountCreateOrEdit}
            />
            <ContactFormModal
                contactToEdit={contactToEdit}
                account={selectedAccount}
                isOpen={isContactModalOpen}
                setIsOpen={setIsContactModalOpen}
                blockedEmails={blocked}
                onSuccess={onContactCreateOrEdit}
                isB2B={clientType === 'B2B'}
                property={property}
            />
            {/* <h3>Payment Processing</h3>
            <hr></hr>
            <iframe src="https://trnsxmobile.com/" width={'100%'} height={'800px'}></iframe> */}
        </div>
    );
};

export default ContactWebform;
